import { type PropsWithChildren, type ReactElement, type ReactNode } from 'react'
import { isEmpty, PITCH_DEPRECATED_PREFERENCES, type Preference } from '@terros/common'
import { Text } from '@mantine/core'
import { useTranslation, useWorkflowForCanvassFeature } from '@front/model'
import { TAlert } from '../terros'
import { usePreferenceContext } from './Context'

const AREA_VISIBILITY_DEPRECATED_MESSAGE =
  'This preference is completely deprecated and no longer used. Manage area visibility with Permissions.'
const WORKFLOW_DEPRECATION_MESSAGE =
  'This preference is deprecated and will be removed in the future. It will be replaced by Workflows.'
const WORKFLOW_DEPRECATED_MESSAGE =
  'This preference is completely deprecated and no longer used. Manage this behavior with Workflows.'

export const DeprecatedCheckWrapper = ({
  children,
  preference,
}: PropsWithChildren<{ preference: Preference }>): ReactNode => {
  const { filter } = usePreferenceContext()
  const { isEnabled, isLoading } = useWorkflowForCanvassFeature()

  if (preference === 'areaVisibility') {
    if (isEmpty(filter)) return
    return (
      <DeprecatedWrapper isCompletelyDeprecated message={AREA_VISIBILITY_DEPRECATED_MESSAGE}>
        {children}
      </DeprecatedWrapper>
    )
  }

  const isDeprecated = PITCH_DEPRECATED_PREFERENCES.some((p) => p === preference)
  if (isLoading) return
  if (!isDeprecated) return children
  if (isEnabled) {
    if (isEmpty(filter)) return
    return (
      <DeprecatedWrapper isCompletelyDeprecated message={WORKFLOW_DEPRECATED_MESSAGE}>
        {children}
      </DeprecatedWrapper>
    )
  }
  return <DeprecatedWrapper message={WORKFLOW_DEPRECATION_MESSAGE}>{children}</DeprecatedWrapper>
}

const DeprecatedWrapper = ({
  children,
  isCompletelyDeprecated = false,
  message = 'This is deprecated and will be removed in the future.',
}: PropsWithChildren<{ isCompletelyDeprecated?: boolean; message?: string }>): ReactElement => {
  const { t } = useTranslation()

  return (
    <TAlert
      variant={isCompletelyDeprecated ? 'error' : 'warning'}
      title={t(isCompletelyDeprecated ? 'Deprecated Preference' : 'Deprecating Preference')}
      noIcon
    >
      <div className='gap-xl flex flex-col'>
        <Text size='sm' c='dimmed'>
          {t(message)}
        </Text>
        {children}
      </div>
    </TAlert>
  )
}
